import { useEffect, useState } from "react";

import { preferences, remember } from "../../services/authService.js";

/**
 * Whether to run the *How to play* walkthrough over this party — phase 24.
 *
 * # 🔴 When it runs, and the two ways to get this wrong
 *
 * On a **live game**, not in the lobby. The lobby is where somebody is choosing
 * a mode and waiting for people; a tutorial there is an interruption before the
 * thing it explains exists. And it must not fire before the preference has been
 * read, or every first render of every party flashes a walkthrough at somebody
 * who dismissed it months ago — which is the failure that makes people hate
 * being taught anything.
 *
 * So the state is genuinely three-valued: `null` while the answer is unknown,
 * and only `true` opens it.
 *
 * # Dismissed once, gone for good
 *
 * The write is per **profile** (`migrations/0034`), which ADR 0027 as amended
 * made possible: every player holds an approved account now, so *"I have seen
 * this"* follows somebody to a new phone rather than starting over.
 *
 * ⚠️ **The write is best effort and the dismissal is local either way.** A
 * failed request must not strand somebody inside a tutorial they have finished;
 * the cost of a lost write is being shown it once more.
 */
export function useWalkthrough(live: boolean): {
  showing: boolean;
  dismiss: () => void;
} {
  // `null` is *not known yet* and is deliberately not `false`: rendering the
  // walkthrough optimistically would flash it at everybody, and rendering
  // nothing optimistically would mean a first-time player who is quick never
  // sees it at all.
  const [seen, setSeen] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void preferences().then(
      (prefs) => {
        if (!cancelled) {
          setSeen(prefs.seen_how_to_play === true);
        }
      },
      () => {
        // A guest with no session, or a server that would not answer. Treated
        // as *seen*: showing a tutorial to somebody whose preference we cannot
        // record means showing it again at their next game, and again after
        // that.
        if (!cancelled) {
          setSeen(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    showing: live && seen === false,
    dismiss: () => {
      setSeen(true);
      void remember({ seen_how_to_play: true }).catch(() => undefined);
    },
  };
}
